/**
 * AnswerInput Component
 * Provides text input field and Send button for submitting student answers
 * Handles validation, loading states, and keyboard shortcuts
 * Modified by US-002: added isShowingTip prop to gate input while learning-tip popup is open.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useViewportHeight } from '@/hooks/useViewportHeight';

/**
 * Props for AnswerInput component
 */
export interface AnswerInputProps {
  /** Callback when user submits an answer */
  /**
   * Submit handler.
   *
   * BUG-20260721: may resolve `false` to signal the turn was rejected, in which
   * case the student's text is restored so they are not asked to retype it. A
   * plain `void` handler (e.g. Math Coach) keeps the original clear-always
   * behaviour, since `await undefined` is not `false`.
   */
  onSubmit: (content: string) => void | Promise<boolean>;

  /** True while answer submission is in progress */
  isSubmitting: boolean;

  /** True if session is completed (disables input) */
  isSessionCompleted: boolean;

  /** True if session timed out due to inactivity (shows timeout-specific message) */
  isTimedOut?: boolean;

  /** True while waiting for the tutor to reply after student submits */
  isWaitingForTutor?: boolean;

  /** Error message to display, or null if no error */
  error?: string | null;

  /** Changes when input should be auto-focused (e.g. last tutor message ID) */
  focusTrigger?: string;

  /** True when a demo limit (attempt or question) has been reached — disables input with demo-specific placeholder */
  isDemoLimitReached?: boolean;

  /** True while a learning-tip popup is open — disables input until the tip is dismissed (US-002) */
  isShowingTip?: boolean;
}

/**
 * Maximum allowed content length (enforced by backend API)
 */
const MAX_CONTENT_LENGTH = 2000;

/**
 * AnswerInput - Text input with Send button for submitting answers
 *
 * Features:
 * - Validates input (non-empty after trimming)
 * - Disables during submission to prevent duplicates
 * - Shows loading spinner while submitting
 * - Supports Enter key for submission
 * - Disables with message when session completed
 * - Displays error messages below input
 *
 * @example
 * ```tsx
 * <AnswerInput
 *   onSubmit={(content) => console.log(content)}
 *   isSubmitting={false}
 *   isSessionCompleted={false}
 *   error={null}
 * />
 * ```
 */
export function AnswerInput({
  onSubmit,
  isSubmitting,
  isSessionCompleted,
  isTimedOut = false,
  isWaitingForTutor = false,
  error,
  focusTrigger,
  isDemoLimitReached = false,
  // US-002: gate input while learning-tip popup is open (AC-1/AC-2/AC-3).
  // Derived from currentPopup?.kind === 'tip' in SessionChatPage; no new state needed.
  isShowingTip = false,
}: AnswerInputProps) {
  // Monitor viewport height for mobile keyboard handling
  useViewportHeight();

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [inputValue, setInputValue] = useState('');

  // Grow the box to fit what the student has written, up to the ceiling set in the
  // maxHeight style below (past which it scrolls vertically).
  //
  // The height must be reset to 'auto' before reading scrollHeight, or the box can
  // only ever grow: scrollHeight of an already-tall element reports the tall height,
  // so it would never shrink back after the value is cleared on submit.
  //
  // The CEILING is CSS, deliberately. useViewportHeight() maintains a --vh custom
  // property on the document root rather than returning anything to a component, so
  // this inherits the mobile-keyboard handling that already works instead of
  // duplicating it in JS.
  //
  // The GROWTH is JS, and deliberately not `field-sizing: content`, which does this
  // in one CSS line. Do not swap it. Support is not the reason — Safari shipped it
  // in 26.2 and Chrome has it. The reason is how it degrades: a browser without it
  // sizes the field AS THOUGH IT WERE FIXED, which is exactly the defect this
  // change exists to fix. That makes it a fine progressive enhancement wherever the
  // fixed size is acceptable and growth is a bonus, and the wrong tool here, where
  // the growth IS the point. The failure would also be silent and would only appear
  // on the browsers we did not test.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [inputValue]);

  // Auto-focus input when focusTrigger changes (e.g. new tutor message)
  useEffect(() => {
    if (focusTrigger && !isSessionCompleted) {
      inputRef.current?.focus();
    }
  }, [focusTrigger, isSessionCompleted]);

  // Check if input has valid content (non-whitespace)
  const isValidInput = inputValue.trim().length > 0;

  // Determine if submission is allowed
  // US-002: isShowingTip added to prevent submission while learning-tip popup is open.
  const canSubmit =
    isValidInput &&
    !isSubmitting &&
    !isSessionCompleted &&
    !isWaitingForTutor &&
    !isDemoLimitReached &&
    !isShowingTip;

  // Determine placeholder text based on session state
  const placeholderText = isTimedOut
    ? 'Session expired -- time limit reached'
    : isSessionCompleted
      ? 'Lesson complete!'
      : isDemoLimitReached
        ? 'Demo limit reached'
        : isWaitingForTutor
          ? 'Waiting for tutor...'
          : 'Type your answer...';

  /**
   * Handle input value change
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;

    // Enforce max length
    if (newValue.length <= MAX_CONTENT_LENGTH) {
      setInputValue(newValue);
    }
  };

  /**
   * Handle answer submission
   */
  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    const trimmedContent = inputValue.trim();

    // Clear optimistically so the box empties instantly. This deliberately differs
    // from ChatInput (which holds the text until success): that component has no
    // optimistic message, whereas useSubmitAnswer adds the student's message to the
    // conversation at this same moment. Holding the text here would leave the answer
    // visible in two places for the whole turn.
    setInputValue('');

    // BUG-20260721: restore the text if the turn was rejected. This is the free-text
    // fallback input, so it serves every question type that is not MCQ (type_13),
    // scaffold (type_12) or a table — i.e. most of them, and the ones with the
    // longest answers. Losing a paragraph of inference writing to a transient tutor
    // timeout, then being asked to "send it again", is the worst version of this
    // failure. MCQAnswerInput and TableAnswerInput never clear their state, so they
    // already survive this and need no equivalent.
    const accepted = await onSubmit(trimmedContent);

    if (accepted === false) {
      // Only restore into a still-empty box: if the student has started typing a
      // replacement while the request was in flight, theirs wins.
      setInputValue((current) => (current === '' ? trimmedContent : current));
    }
  };

  /**
   * Handle Enter key press for submission.
   *
   * The `!e.shiftKey` guard predates the textarea and was inert while this was an
   * `<input>` (a single-line input has no newline for Shift+Enter to insert). On a
   * textarea it starts doing the job it was always written for: Enter submits,
   * Shift+Enter inserts a newline. preventDefault is what stops Enter ALSO inserting
   * one on the way out.
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && canSubmit) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Show the gentle otter hint only while the student can actually answer.
  const showHint =
    !isSessionCompleted && !isTimedOut && !isWaitingForTutor && !isDemoLimitReached && !error;

  return (
    <div className="sticky bottom-0 z-10 border-t border-[#E7D8C8] bg-otter-paper/90 p-3 backdrop-blur-md pb-[env(safe-area-inset-bottom)] md:p-4">
      <div className="mx-auto max-w-3xl">
        {/* Input and button container.
            items-END, not items-center: the Send button is a fixed h-12 and the box
            grows, so centring would float the button to the vertical middle of a tall
            field. Bottom-aligned keeps it beside the last line the student typed. */}
        <div className="flex items-end gap-2">
          <Textarea
            ref={inputRef}
            rows={2}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={
              isSubmitting ||
              isSessionCompleted ||
              isWaitingForTutor ||
              isDemoLimitReached ||
              isShowingTip
            }
            placeholder={placeholderText}
            // No h-12 here, unlike the Input this replaced: a fixed height would pin
            // the textarea to one line and silently undo the whole change.
            // resize-none because the height is ours to manage; the browser's drag
            // handle would fight the auto-grow effect above.
            className="flex-1 resize-none overflow-y-auto rounded-2xl border-otter-sand bg-white px-4 py-3 shadow-sm focus-visible:ring-otter-orange-500"
            // The ceiling: about four lines, but never more than a quarter of the
            // viewport that is actually left. On a phone with the keyboard up, --vh
            // has already shrunk, so this yields to the conversation rather than
            // eating it. Both halves matter — the rem alone is wrong on a short
            // screen, the vh alone is wrong on a tall one.
            style={{ maxHeight: 'min(7rem, calc(var(--vh, 1vh) * 25))' }}
            aria-label="Answer input"
            maxLength={MAX_CONTENT_LENGTH}
          />

          <Button
            variant="warm"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="h-12 min-w-[80px] rounded-2xl px-5"
            aria-label="Send answer"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending
              </>
            ) : (
              'Send'
            )}
          </Button>
        </div>

        {/* Character count indicator */}
        {inputValue.length > 0 && (
          <div className="mt-1 text-right text-xs text-pebbles-500">
            {inputValue.length}/{MAX_CONTENT_LENGTH}
          </div>
        )}

        {/* Gentle otter encouragement — only when the student can answer and there's no error */}
        {showHint && inputValue.length === 0 && (
          // BUG-20260709-194338 (T-5): select-none + pointer-events-none so this
          // hint cannot be selected/copied into the answer box (the index-43
          // vector — a student copying the otter hint and submitting it as an
          // answer, which the backend then force-graded as a genuine response).
          <p
            className="mt-2 flex select-none items-center gap-1.5 text-xs text-pebbles-500"
            style={{ userSelect: 'none', pointerEvents: 'none' }}
          >
            <span aria-hidden="true">🦦</span> Take your time — look back at the passage if you need
            to.
          </p>
        )}

        {/* Error message */}
        {error && (
          <div className="mt-2 text-sm text-red-600" role="alert" aria-live="polite">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
